"use client";

import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Button,
  Alert,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import OverviewPage from "./OverviewPage";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { fetchAccountsName } from "../customerManagement/customerManagementThunks";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import sampleExcelPath from "./sample-whitelist.xlsx";

const actionTypes = [
  { value: "sms", label: "SMS" },
  { value: "data", label: "Data" },
];

const instructionsConfig = {
  sms: {
    title: "Instructions for SMS Bulk Whitelisting",
    items: [
      "Template example (msisdn, group_name) e.g('917849037653197, my_group)",
      "Ensure the headers are exactly as shown in the example above or download the template below",
      "All whitelist entries should be unique",
      "Accepted whitelist entries are: 10 and 13 digits (Do not add prefix like 0, 91 and +91 to the entries)",
      "The entries (1111111111,2222222222,3333333333) are only for illustration purpose and can edited as per your requirement",
      "Duplicate MSISDNs are not accepted",
      "You can also add, modify & delete whitelist entries as per business requirements",
      "Keep the fields blank for bulk deletion of whitelist entries",
      "Suggestion: Download the template by clicking on the button below, make required alterations and upload.",
    ],
    requiredHeaders: [
      "msisdn",
      "group_name",
      //   "sms_outgoing_2",
      //   "sms_outgoing_3",
      //   "sms_outgoing_4",
      //   "sms_incoming_1",
      //   "sms_incoming_2",
      //   "sms_incoming_3",
      //   "sms_incoming_4",
    ],
  },
  data: {
    title: "Instructions for Data Bulk Whitelisting",
    items: [
      "Template example (msisdn, group_name)  e.g('917849037653197, my_group)",
      "Ensure the headers are exactly as shown in the example above or download the template below",
      "All whitelist entries should be unique",
      "Accepted whitelist entries are: 10 and 13 digits (Do not add prefix like 0, 91 and +91 to the entries)",
      "The entries (1111111111,2222222222,3333333333) are only for illustration purpose and can edited as per your requirement",
      "Duplicate MSISDNs are not accepted",
      "You can also add, modify & delete whitelist entries as per business requirements",
      "Keep the fields blank for bulk deletion of whitelist entries",
      "Suggestion: Download the template by clicking on the button below, make required alterations and upload.",
    ],
    requiredHeaders: [
      "msisdn",
      "group_name",
      //   "data_outgoing_2",
      //   "data_outgoing_3",
      //   "data_outgoing_4",
      //   "data_incoming_1",
      //   "data_incoming_2",
      //   "data_incoming_3",
      //   "data_incoming_4",
    ],
  },
};

function ExcelUpload({
  formData = {},
  onFormDataChange = () => {},
  onNext = () => {},
}) {
  const [errors, setErrors] = useState({});
  const [instructionsExpanded, setInstructionsExpanded] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [localFormData, setLocalFormData] = useState(formData || {});
  const [activeStep, setActiveStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const steps = ["Bulk Whitelisting", "Overview"];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  // Update local state whenever formData prop changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setLocalFormData(formData);
    }
  }, [formData]);

  const handleInputChange = (field, value) => {
    // Update local state with complete merged object
    const updatedFormData = { ...localFormData, [field]: value };
    setLocalFormData(updatedFormData);

    // Call prop function if it exists - pass complete object
    if (typeof onFormDataChange === "function") {
      onFormDataChange(updatedFormData);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!localFormData.accountName) {
      newErrors.accountName = "Account Name is required";
    }

    if (!localFormData.actionType) {
      newErrors.actionType = "Action Type is required";
    }

    if (!localFormData.uploadedFile) {
      newErrors.file = "Please upload a file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateExcelHeaders = (headers, actionType) => {
    const requiredHeaders =
      instructionsConfig[actionType]?.requiredHeaders || [];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
    }

    return true;
  };

  const handleFileUpload = async (file) => {
    try {
      if (!localFormData.actionType) {
        setErrors((prev) => ({
          ...prev,
          file: "Please select Action Type first",
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error("Only CSV and Excel files are supported");
      }

      // Validate file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error("File size should not exceed 50 MB");
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length === 0) {
            throw new Error("File is empty");
          }

          const headers = jsonData[0].map((header) =>
            header.toString().toLowerCase()
          );
          const requiredHeaders =
            instructionsConfig[localFormData.actionType].requiredHeaders;

          validateExcelHeaders(headers, localFormData.actionType);

          // Validate record count
          const nonEmptyRows = jsonData
            .slice(1)
            .filter((row) =>
              row.some(
                (cell) => cell !== undefined && cell !== null && cell !== ""
              )
            );

          // Now check if the actual data (excluding headers and empty rows) exceeds limit
          if (nonEmptyRows.length > 1000) {
            throw new Error("Maximum 1000 records allowed");
          }

          // Use nonEmptyRows for data processing
          const dataRows = nonEmptyRows;

          // Process data rows and add status
          const processedData = dataRows.map((row) => {
            // Convert row array to object using headers
            const rowObj = {};
            headers.forEach((header, index) => {
              rowObj[header] = row[index]?.toString() || "";
            });

            // Add status based on validation (simple validation - just check if msisdn exists)
            rowObj.status = rowObj.msisdn ? "success" : "error";

            return rowObj;
          });

          // Create complete updated form data
          const updatedFormData = {
            ...localFormData,
            uploadedFile: file,
            fileData: jsonData,
            processedData: processedData,
          };

          // Update local state first
          setLocalFormData(updatedFormData);
          setFileName(file.name);

          // Then call the prop function if it exists
          if (typeof onFormDataChange === "function") {
            onFormDataChange(updatedFormData);
          }

          setErrors((prev) => ({ ...prev, file: "" }));
        } catch (error) {
          setErrors((prev) => ({ ...prev, file: error.message }));
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setErrors((prev) => ({ ...prev, file: error.message }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleNext = () => {
    if (validateForm()) {
      setActiveStep(1);
      if (typeof onNext === "function") {
        onNext();
      }
    }
  };

  const handlePrevious = () => {
    setActiveStep(0);
  };

  const handleSubmitClick = () => {
    // No longer needed here, as it's handled in OverviewPage
  };

  const handleSubmit = async () => {
    // No longer needed here, as it's handled in OverviewPage
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    // Reset the form and go back to step 0
    setLocalFormData({});
    setActiveStep(0);
    navigate("/bulkWhitelist");
  };

  const currentInstructions = localFormData?.actionType
    ? instructionsConfig[localFormData.actionType]
    : null;

  const dispatch = useDispatch();
  const { accountNames, loading } = useSelector(
    (state) => state.customerManagement.accounts
  );
  useEffect(() => {
    dispatch(fetchAccountsName());
  }, [dispatch]);

  useEffect(() => {
    if (accountNames?.length === 1 && !localFormData?.accountName) {
      handleInputChange("accountName", accountNames[0].accountName);
    }
  }, [accountNames, localFormData?.accountName, handleInputChange]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          marginTop: 12,
          px: 7, // shorthand for padding-left + padding-right
        }}
      >
        {/* Stepper */}
        <Box
          sx={{
            width: "100%",
            mb: 4,
            display: "flex",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--bg)",
              color: "white",
              padding: "10px 20px",
              borderRadius: "4px 0 0 4px",
              position: "relative",
              zIndex: 2,
            }}
          >
            <Typography
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: activeStep === 0 ? "white" : "#A0AEC0",
                color: activeStep === 0 ? "var(--bg)" : "white",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                marginRight: 1,
              }}
            >
              1
            </Typography>
            <Typography>Bulk Whitelisting</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: activeStep === 1 ? "var(--bg)" : "#E2E8F0",
              color: activeStep === 1 ? "white" : "var(--bg)",
              padding: "10px 20px",
              borderRadius: "0 4px 4px 0",
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                left: -10,
                width: 20,
                height: "100%",
                backgroundColor: "inherit",
                clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                transform: "rotate(-90deg)",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: activeStep === 1 ? "white" : "#A0AEC0",
                color: activeStep === 1 ? "#2D3748" : "#4A5568",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                marginRight: 1,
              }}
            >
              2
            </Typography>
            <Typography>Overview</Typography>
          </Box>
        </Box>

        {/* Content Area - with flex-grow to push buttons to bottom */}
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {activeStep === 0 ? (
            <Box sx={{ p: 2 }}>
              {/* Basic Details */}
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Basic Details
              </Typography>

              <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
                <Autocomplete
                  options={accountNames || []}
                  value={
                    accountNames.find(
                      (opt) => opt.accountName === localFormData?.accountName
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleInputChange(
                      "accountName",
                      newValue?.accountName || ""
                    )
                  }
                  getOptionLabel={(option) => option.accountName || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Account Name"
                      placeholder="Select or search account"
                      variant="outlined"
                      error={!!errors.accountName}
                      helperText={errors.accountName}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  sx={{ flex: 1 }}
                  readOnly={accountNames && accountNames.length === 1}
                  disableClearable
                />

                <Autocomplete
                  options={actionTypes}
                  value={
                    actionTypes.find(
                      (opt) => opt.value === localFormData?.actionType
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    handleInputChange("actionType", newValue?.value || "");
                    setLocalFormData((prev) => ({
                      ...prev,
                      uploadedFile: null,
                      fileData: null,
                      processedData: null,
                    }));
                    setFileName("");
                    setErrors({});
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  getOptionLabel={(option) => option.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Action Type"
                      placeholder="Select or search action type"
                      variant="outlined"
                      error={!!errors.actionType}
                      helperText={errors.actionType}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  sx={{ flex: 1 }}
                />
              </Box>

              {/* Instructions */}
              {currentInstructions && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 4,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setInstructionsExpanded(!instructionsExpanded)
                    }
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {currentInstructions.title}
                    </Typography>
                    <IconButton size="small">
                      {instructionsExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={instructionsExpanded}>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <List dense>
                        {currentInstructions.items.map((item, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={`• ${item}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { lineHeight: 1.5 },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Collapse>
                </Paper>
              )}

              {/* Bulk Upload */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Bulk Upload
                </Typography>
                <a href={sampleExcelPath} download>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textDecoration: "underline", textTransform: "none" }}
                  >
                    Download sample file
                  </Button>
                </a>
              </Box>

              {/* File Upload Area */}
              <Paper
                elevation={0}
                sx={{
                  border: dragActive
                    ? "2px dashed #1976d2"
                    : "2px dashed #e0e0e0",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  backgroundColor: dragActive ? "#f3f8ff" : "#fafafa",
                  cursor: "pointer",
                  mb: 2,
                  transition: "all 0.3s ease",
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                <DescriptionIcon
                  sx={{ fontSize: 48, color: "#4caf50", mb: 2 }}
                />

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                  Upload
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  <strong>Drag</strong> and <strong>Drop</strong> your file here
                  or <strong>Browse</strong> to upload
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Supported Files and Sizes are <strong>CSV and Excel</strong>{" "}
                  maximum number of records is 1000 with max size of{" "}
                  <strong>10 MB</strong>
                </Typography>

                {localFormData?.uploadedFile && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "#e8f5e8",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="success.main">
                      ✓ {localFormData.uploadedFile.name} uploaded successfully
                    </Typography>
                  </Box>
                )}
              </Paper>

              {errors.file && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.file}
                </Alert>
              )}

              <Typography
                variant="caption"
                color="error"
                sx={{ float: "right", mb: 2 }}
              >
                *Mandatory
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <OverviewPage
                fileData={localFormData?.processedData || []}
                fileName={fileName}
                accountName={localFormData?.accountName}
                actionType={localFormData?.actionType}
                uploadedFile={localFormData?.uploadedFile}
                onPrevious={handlePrevious}
                onSubmit={(responseData) => {
                  // Handle successful submission
                  setLocalFormData({});
                  setActiveStep(0);
                  if (typeof onNext === "function") {
                    onNext(responseData);
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Action Buttons - Only shown on the first step */}
        {activeStep === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                px: 4,
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "#666",
              }}
            >
              cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: "var(--bg)",
                "&:hover": {
                  backgroundColor: "var(--bg)",
                },
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}

export default ExcelUpload;
